import { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { X, ShieldAlert } from 'lucide-react';
import { localApi } from '../utils/localApi';

interface PNBulletinFormProps {
    driverName: string;
    onClose: () => void;
    onSuccess: () => void;
}

export default function PNBulletinForm({ driverName, onClose, onSuccess }: PNBulletinFormProps) {
    const [seqNum, setSeqNum] = useState('01');
    const [receivedNum, setReceivedNum] = useState('');
    const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [time, setTime] = useState(() => {
        const now = new Date();
        return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    });
    const [trainNumber, setTrainNumber] = useState('');
    const [pnNumber, setPnNumber] = useState('');
    const [lineName, setLineName] = useState<'Centrale' | 'Balagne'>('Centrale');
    const [signature, setSignature] = useState(driverName);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchNextNumber();
    }, []);

    const fetchNextNumber = async () => {
        try {
            const next = await localApi.getNextBulletinNumber();
            setSeqNum(next);
        } catch (err) {
            console.error("Erreur calcul numéro transmis:", err);
        }
    };

    const handleSignAndSave = async () => {
        if (!receivedNum || !trainNumber || !pnNumber || !signature) {
            alert("Veuillez remplir tous les champs obligatoires (*) avant de signer le bulletin.");
            return;
        }

        setLoading(true);

        try {
            // 1. Générer le PDF en local avec jsPDF
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            // Marges et bordures
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.5);
            doc.rect(5, 5, 200, 287); // Cadre global de la page

            // En-tête du formulaire
            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(10);
            doc.text('CHEMINS DE FER DE LA CORSE (C.F.C.)', 10, 15);
            doc.setFontSize(12);
            doc.text('FORMULAIRE N° 12', 195, 15, { align: 'right' });

            doc.setLineWidth(0.3);
            doc.line(10, 18, 200, 18);

            // Titre Principal
            doc.setFontSize(14);
            doc.text('BULLETIN D’ORDRES ET DE PRESCRIPTION POUR PN', 105, 27, { align: 'center' });
            
            doc.setFont('Helvetica', 'normal');
            doc.setFontSize(9);
            doc.text('(A remettre au conducteur du train ou de l’engin de manœuvre)', 105, 33, { align: 'center' });

            doc.line(40, 36, 170, 36);

            // Métadonnées
            doc.setFontSize(10);
            doc.text(`Numéro d’ordre transmis : ${seqNum}`, 10, 45);
            doc.text(`Numéro d’ordre reçu : ${receivedNum}`, 105, 45);

            doc.text(`Date : ${date.split('-').reverse().join('/')}`, 10, 52);
            doc.text(`Heure : ${time}`, 105, 52);

            doc.setFont('Helvetica', 'bold');
            doc.text(`Train n° : ${trainNumber}`, 10, 60);

            doc.line(10, 74, 200, 74);

            // Contenu de la prescription
            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(11);
            doc.text(`Le PN n° ${pnNumber} n’a pas fonctionné normalement à SAL (1)`, 10, 83);
            
            doc.setFont('Helvetica', 'normal');
            doc.setFontSize(10.5);
            const textContent = `Ordre est donné à l'agent de conduite du train n° de s'arrêter avant le PN n° ligne Centrale/Balagne (rayer la mention inutile) et quelle que soit l'indication présentée par le signal de contrôle de fermeture des barrières, de le franchir avec prudence et en sifflant longuement après que le Chef de Train ait pris les dispsoitions pour arrêter les circulations routières. Motif : Raté de fermeture.`;
            
            const splitText = doc.splitTextToSize(textContent, 185);
            doc.text(splitText, 10, 93);

            doc.line(10, 140, 200, 140);

            // Notes de bas de page
            doc.setFontSize(8.5);
            const noteText = `(1) SAL : Signalisation Automatique Lumineuse ( demi barrières). L’agent transmettant l’ordre doit biffer la mention « à SAL » inutile`;
            const splitNote = doc.splitTextToSize(noteText, 185);
            doc.text(splitNote, 10, 147);

            // Signatures
            doc.setFontSize(10);
            doc.text('Vu et pris connaissance :', 10, 175);
            doc.setFont('Helvetica', 'bold');
            doc.text(`Le Conducteur : ${signature}`, 10, 182);
            doc.setFont('Helvetica', 'normal');
            doc.text(`[Signé électroniquement]`, 10, 188);

            doc.line(10, 205, 200, 205);

            // Rappels de sécurité / Observations
            doc.setFont('Helvetica', 'bold');
            doc.setFontSize(9);
            doc.text('Observation :', 10, 215);
            doc.setFont('Helvetica', 'normal');
            const obsText = `le conducteur de tout train ou engin de manœuvre doit prendre à l’égard du passage à niveau visé ci-dessus les mesures prescrites par la consigne particulière PN-`;
            const splitObs = doc.splitTextToSize(obsText, 185);
            doc.text(splitObs, 35, 215);

            // Sauvegarde locale du PDF sur la tablette
            doc.save(`bulletin_pn_${seqNum}.pdf`);

            // 2. Transférer le PDF en Base64 au serveur PHP
            const pdfBase64 = doc.output('datauristring').split(',')[1];

            const response = await localApi.saveDriverBulletin({
                bulletin_type: 'Raté de fermeture de PN à SAL',
                sequence_number: seqNum,
                received_number: receivedNum,
                bulletin_date: date,
                bulletin_time: time,
                train_number: trainNumber,
                issuer_info: '',
                pn_number: pnNumber,
                pk_value: '',
                pdf_base64: pdfBase64
            });

            if (response.error) throw new Error(response.error);

            // 3. Envoi à la boîte noire
            await localApi.sendBlackboxEvent({
                id: Date.now().toString(),
                driver_name: driverName,
                event_type: 'BULLETIN_PN_SIGNED',
                timestamp: Math.floor(Date.now() / 1000),
                details: `Signature du bulletin PN N°${pnNumber} (Transmis: ${seqNum}, Reçu: ${receivedNum}, Train: ${trainNumber})`
            });

            alert("Bulletin enregistré et signé avec succès. Une copie PDF a été téléchargée sur votre appareil.");
            onSuccess();
        } catch (err: any) {
            console.error(err);
            alert("Erreur lors de la signature du bulletin: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[6000] bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-[#0f172a] border border-white/10 rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                <header className="px-6 py-4 bg-slate-900 border-b border-white/5 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <ShieldAlert className="text-yellow-500 w-5 h-5" />
                        <h2 className="text-white font-black uppercase text-sm tracking-wider">Raté de fermeture de PN à SAL</h2>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-lg bg-white/5 text-slate-400 hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {/* Partie Administrative Compacte */}
                    <div className="grid grid-cols-2 gap-4 bg-slate-800/20 border border-white/5 p-4 rounded-xl">
                        <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">N° ordre transmis (Auto)</label>
                            <input
                                type="text"
                                value={seqNum}
                                readOnly
                                className="bg-slate-900 border border-white/5 text-slate-500 rounded-lg px-3 py-2 font-mono text-xs outline-none cursor-not-allowed"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">N° ordre reçu <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={receivedNum}
                                onChange={(e) => setReceivedNum(e.target.value)}
                                placeholder="Ex: 12"
                                className="bg-slate-900 border border-white/10 text-white rounded-lg px-3 py-2 font-mono text-xs outline-none focus:border-blue-500/50"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Date</label>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="bg-slate-900 border border-white/10 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-500/50"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Heure</label>
                            <input
                                type="time"
                                value={time}
                                onChange={(e) => setTime(e.target.value)}
                                className="bg-slate-900 border border-white/10 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-500/50"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Train N° <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={trainNumber}
                                onChange={(e) => setTrainNumber(e.target.value)}
                                placeholder="Ex: 802"
                                className="bg-slate-900 border border-white/10 text-white rounded-lg px-3 py-2 font-mono text-xs outline-none focus:border-blue-500/50"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Ligne</label>
                            <select
                                value={lineName}
                                onChange={(e) => setLineName(e.target.value as any)}
                                className="bg-slate-900 border border-white/10 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-500/50"
                            >
                                <option value="Centrale">Ligne Centrale</option>
                                <option value="Balagne">Ligne de la Balagne</option>
                            </select>
                        </div>
                    </div>

                    {/* Partie Prescription Légale Compacte */}
                    <div className="bg-slate-900 border border-white/10 p-4 rounded-xl space-y-3">
                        <div className="flex items-center gap-3 border-b border-white/5 pb-2">
                            <div className="flex flex-col gap-1">
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">N° de PN <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={pnNumber}
                                    onChange={(e) => setPnNumber(e.target.value)}
                                    placeholder="Ex: 5"
                                    className="bg-slate-800 border border-white/10 text-white rounded-lg px-2.5 py-1.5 w-24 text-center font-bold text-xs outline-none focus:border-blue-500/50"
                                />
                            </div>
                        </div>

                        <div className="text-slate-300 text-[11px] leading-relaxed space-y-2 font-serif p-3 bg-black/20 rounded-lg">
                            <p className="font-bold underline text-white">Le PN n° {pnNumber || '____'} n’a pas fonctionné normalement à SAL (1)</p>
                            <p className="italic">
                                Ordre est donné à l'agent de conduite du train n° de s'arrêter avant le PN n° ligne Centrale/Balagne (rayer la mention inutile) et quelle que soit l'indication présentée par le signal de contrôle de fermeture des barrières, de le franchir avec prudence et en sifflant longuement après que le Chef de Train ait pris les dispsoitions pour arrêter les circulations routières. Motif : Raté de fermeture.
                            </p>
                            <p className="text-[9px] text-slate-500 font-sans mt-2">
                                (1) SAL : Signalisation Automatique Lumineuse ( demi barrières). L’agent transmettant l’ordre doit biffer la mention « à SAL » inutile
                            </p>
                        </div>
                    </div>

                    {/* Signature Compacte */}
                    <div className="flex flex-col gap-1 bg-slate-800/10 border border-white/5 p-4 rounded-xl">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Vu et pris connaissance - Signature Conducteur <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            value={signature}
                            onChange={(e) => setSignature(e.target.value)}
                            className="bg-slate-900 border border-white/10 text-white rounded-lg px-3 py-2 text-xs font-bold tracking-wide outline-none focus:border-blue-500/50"
                        />
                    </div>
                </div>

                <footer className="px-5 py-3 bg-slate-900 border-t border-white/5 flex justify-end gap-2 shrink-0">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleSignAndSave}
                        disabled={loading}
                        className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:text-slate-400 text-white rounded-lg text-[10px] font-black uppercase tracking-widest transition-all shadow-lg shadow-blue-900/30 flex items-center gap-2"
                    >
                        {loading ? 'Signature...' : 'Signer et Enregistrer'}
                    </button>
                </footer>
            </div>
        </div>
    );
}
